import React, { useState, useEffect, useCallback, useMemo } from 'react';
import FreeShoeIcon from '../components/FreeShoeIcon';
import Modal from '../components/Modal';
import {
  syncSheets, quickSyncSheets, resyncAll, getAppointments, updateAppointment,
  getErrors, retryAllErrors, correctError, getAppointmentChangeLog,
  updateAppointmentStatus, duplicateAppointment,
} from '../api/appointments';
import { getPackableAppointments } from '../api/dispatch';
import { getRiderAssignmentJobs } from '../api/riderAssignment';
import { listPastOrders } from '../api/orderHistory';
import { useAgeSizes } from '../api/meta';
import SelectedDesigns from '../components/SelectedDesigns';
import NewBookingModal from '../components/NewBookingModal';
import { useNavigation } from '../context/NavigationContext';
import { previewZocsSync } from '../api/skipzyAdmin';

const IS_ADMIN = () => {
  try { return JSON.parse(localStorage.getItem('zocs_auth') || '{}').access_level === 'admin'; }
  catch { return false; }
};
const IS_CORE_TEAM = () => {
  try { return ['admin', 'core_team'].includes(JSON.parse(localStorage.getItem('zocs_auth') || '{}').access_level); }
  catch { return false; }
};

// Maps raw column names (as stored in AppointmentChangeLog.field_changed) to human-readable labels.
const APPT_FIELD_LABELS = {
  customer_status: 'Customer status',
  operational_status: 'Operational status',
  current_scheduled_date: 'Date',
  current_scheduled_time: 'Time slot',
  address_line_1: 'Address line 1',
  address_line_2: 'Address line 2',
  area: 'Area',
  pincode: 'Pincode',
  google_maps_link: 'Google Maps link',
  notes: 'Notes',
  source_l1: 'Source',
  referral_code: 'Referral Code',
  cancellation_reason_tag: 'Cancellation reason',
  cancellation_reason_message: 'Cancellation message',
  duplicated_from: 'Duplicated from',
  duplicated_to: 'Duplicated to',
};
const CHILD_FIELD_LABELS = {
  size: 'Size (this appointment)',
  current_size: 'Current size (child baseline)',
  use_case_pref_override: 'Use-case prefs',
  style_pref_override: 'Style prefs',
  avoid_tags_override: 'Avoid tags',
  brand_pref: 'Brand prefs',
  notes: 'Child notes',
  created: 'Child added',
};

// Parses field_changed like "child[uuid].size" → { childId, field } or null for appointment-level.
function parseFieldChanged(raw) {
  const m = raw && raw.match(/^child\[([^\]]+)\]\.(.+)$/);
  return m ? { childId: m[1], field: m[2] } : null;
}

function describeField(raw, appointment) {
  const parsed = parseFieldChanged(raw);
  if (!parsed) return APPT_FIELD_LABELS[raw] || raw;
  const label = CHILD_FIELD_LABELS[parsed.field] || parsed.field;
  // Resolve childId → "Girl 3-4Y" by scanning appointment.children
  const ac = (appointment?.children || []).find(c => c.appointment_child_id === parsed.childId);
  if (!ac) return `${label} — child`;
  const gender = ac.gender ? (ac.gender.charAt(0).toUpperCase() + ac.gender.slice(1)) : '';
  const suffix = [gender, ac.size].filter(Boolean).join(' ');
  return suffix ? `${label} — ${suffix}` : label;
}

const SOURCE_L1_OPTIONS = [
  { value: '', label: '— not set —' },
  { value: 'offline_activation', label: 'Offline Activation' },
  { value: 'online_ads', label: 'Online Ads' },
  { value: 'repeat', label: 'Repeat' },
  { value: 'referral', label: 'Referral' },
];

const OP_STATUSES = ['scheduled','queued','pack_assigned','dispatched','in_progress','completed','cancelled','cancelled_after_dispatch','rto','dol'];
// `queued` is set by assigning a bolt-on to a trip, never typed in by hand.
const EDITABLE_OP_STATUSES = OP_STATUSES.filter(s => s !== 'queued');
const CUST_STATUSES = ['booked','message_sent','confirmed_on_call_or_text','confirmed_on_delivery_day','cancelled','delivered','rto','customer_not_on_whatsapp','junk','duplicate'];

// Standard cancellation reasons offered when Customer Status is set to
// "cancelled" — these push the exact message to the customer's phone if the
// appointment came from the app. "other" has no canned text; staff types it.
const CANCELLATION_REASONS = [
  { value: '', label: '— select a reason —' },
  { value: 'DOL', label: 'DOL (could not connect for delivery)' },
  { value: 'no_response', label: 'No Response (could not confirm by phone)' },
  { value: 'outside_delivery_area', label: 'Outside Delivery Area' },
  { value: 'customer_requested', label: 'Customer Requested' },
  { value: 'other', label: 'Other — type a message' },
];

// Mirrors CANCELLATION_REASON_MESSAGES in the backend
// (routers/appointments.py) — keep both in step. Shown here so staff sees
// exactly what the customer will get before saving, without a round trip;
// the backend re-applies the same canned text server-side regardless, so a
// stale copy here can only affect the preview, never what's actually sent.
const CANCELLATION_REASON_MESSAGES = {
  DOL: "Hi! Our teammate was near your location but we couldn't connect with you for delivery. We have cancelled the session. Please call or message us to reschedule this delivery!",
  no_response: 'Hi We tried calling you to confirm your order for today. We are cancelling the session for now. Please feel free to message or call us and we will try to get you the pack as soon as possible.',
  outside_delivery_area: 'Hi! Thanks for booking with Skipzy, but unfortunately, your location is outside our delivery area. We will let you know once we are active in your area. Sorry for the inconvenience.',
  customer_requested: 'Hi! As requested by you, we are cancelling your session. Please let us know if you would like to reschedule it at any point.',
};

const OP_COLOURS = {
  scheduled: 'bg-blue-100 text-blue-700',
  // Same as dispatched: a queued bolt-on is on its way, riding on another order's bag.
  queued: 'bg-purple-100 text-purple-700',
  pack_assigned: 'bg-yellow-100 text-yellow-700',
  dispatched: 'bg-purple-100 text-purple-700',
  in_progress: 'bg-orange-100 text-orange-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-gray-100 text-gray-500',
  cancelled_after_dispatch: 'bg-gray-100 text-gray-500',
  rto: 'bg-red-100 text-red-700',
  dol: 'bg-red-100 text-red-700',
};

const CUST_COLOURS = {
  booked: 'bg-blue-50 text-blue-600',
  message_sent: 'bg-yellow-50 text-yellow-600',
  confirmed_on_call_or_text: 'bg-green-50 text-green-700',
  confirmed_on_delivery_day: 'bg-green-100 text-green-800',
  cancelled: 'bg-gray-100 text-gray-500',
  delivered: 'bg-green-200 text-green-900',
  rto: 'bg-red-100 text-red-700',
  customer_not_on_whatsapp: 'bg-orange-50 text-orange-600',
  junk: 'bg-gray-100 text-gray-400',
  duplicate: 'bg-gray-100 text-gray-400',
};

function Badge({ value, colourMap }) {
  const cls = colourMap[value] || 'bg-gray-100 text-gray-500';
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cls}`}>{value ? value.replace(/_/g, ' ') : '—'}</span>;
}

function formatChild(child) {
  const gender = child.gender ? (child.gender.charAt(0).toUpperCase() + child.gender.slice(1)) : '';
  return child.size + (gender ? ' | ' + gender : '');
}

function mapsUrl(address) {
  return 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(address);
}

// ── Detail Modal ──────────────────────────────────────────────────────────

function AppointmentDetailModal({ appointment, onClose, onSaved }) {
  // Snapshot the original values for diffing.
  const original = appointment;
  const zoddleSizes = useAgeSizes();
  const { navigate } = useNavigation();

  function goToOrderOps(ooAction) {
    navigate('orderops', { ooAction, appointmentId: appointment.appointment_id });
    onClose();
  }

  const [apptForm, setApptForm] = useState({
    customer_status: appointment.customer_status || '',
    operational_status: appointment.operational_status || '',
    current_scheduled_date: appointment.scheduled_date || '',
    current_scheduled_time: appointment.scheduled_time || '',
    address_line_1: appointment.address_line_1 || '',
    address_line_2: appointment.address_line_2 || '',
    area: appointment.area || '',
    pincode: appointment.pincode || '',
    google_maps_link: appointment.google_maps_link || '',
    notes: appointment.notes || '',
    source_l1: appointment.source_l1 || '',
    referral_code: appointment.referral_code || '',
    cancellation_reason_tag: appointment.cancellation_reason_tag || '',
    cancellation_reason_message: appointment.cancellation_reason_message || '',
  });
  const canDuplicate = IS_CORE_TEAM() && !!appointment.zcp_appointment_uid && (appointment.children || []).length > 0;
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [childrenForm, setChildrenForm] = useState(() => {
    const map = {};
    (appointment.children || []).forEach(c => {
      map[c.appointment_child_id] = {
        size: c.size || '',
        use_case_pref: c.use_case_pref_override || '',
        style_pref: c.style_pref_override || '',
        avoid_tags: c.avoid_tags_override || '',
        brand_pref: c.brand_pref_override || '',
        notes: c.notes || '',
      };
    });
    return map;
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  function setApptField(key, val) { setApptForm(f => ({ ...f, [key]: val })); }
  function setChildField(acId, key, val) {
    setChildrenForm(m => ({ ...m, [acId]: { ...m[acId], [key]: val } }));
  }
  // Picking a standard reason fills in its canned message; "other" (or
  // clearing the selection) empties it for free typing.
  function setCancellationReasonTag(tag) {
    setApptForm(f => ({
      ...f,
      cancellation_reason_tag: tag,
      cancellation_reason_message: CANCELLATION_REASON_MESSAGES[tag] || '',
    }));
  }

  async function handleSave() {
    setSaving(true); setError(null);
    try {
      if (apptForm.customer_status === 'cancelled') {
        if (!apptForm.cancellation_reason_tag) {
          setError('Pick a cancellation reason before saving.');
          setSaving(false);
          return;
        }
        if (apptForm.cancellation_reason_tag === 'other' && !apptForm.cancellation_reason_message.trim()) {
          setError('Type the message to send for "Other".');
          setSaving(false);
          return;
        }
      }
      // Build appointment-level diff
      const apptPayload = {};
      const origMap = {
        customer_status: original.customer_status || '',
        operational_status: original.operational_status || '',
        current_scheduled_date: original.scheduled_date || '',
        current_scheduled_time: original.scheduled_time || '',
        address_line_1: original.address_line_1 || '',
        address_line_2: original.address_line_2 || '',
        area: original.area || '',
        pincode: original.pincode || '',
        google_maps_link: original.google_maps_link || '',
        notes: original.notes || '',
        source_l1: original.source_l1 || '',
        referral_code: original.referral_code || '',
        cancellation_reason_tag: original.cancellation_reason_tag || '',
        cancellation_reason_message: original.cancellation_reason_message || '',
      };
      Object.entries(apptForm).forEach(([k, v]) => {
        if (v !== origMap[k]) apptPayload[k] = v === '' ? null : v;
      });

      // Build per-child diff
      const childrenPayload = [];
      (original.children || []).forEach(c => {
        const form = childrenForm[c.appointment_child_id];
        if (!form) return;
        const diff = { appointment_child_id: c.appointment_child_id };
        let touched = false;
        const pairs = [
          ['size', c.size || ''],
          ['use_case_pref', c.use_case_pref_override || ''],
          ['style_pref', c.style_pref_override || ''],
          ['avoid_tags', c.avoid_tags_override || ''],
          ['brand_pref', c.brand_pref_override || ''],
          ['notes', c.notes || ''],
        ];
        pairs.forEach(([field, origVal]) => {
          if (form[field] !== origVal) {
            diff[field] = form[field] === '' ? null : form[field];
            touched = true;
          }
        });
        if (touched) childrenPayload.push(diff);
      });

      const payload = {};
      if (Object.keys(apptPayload).length > 0) payload.appointment = apptPayload;
      if (childrenPayload.length > 0) payload.children = childrenPayload;
      if (Object.keys(payload).length === 0) {
        setError('No changes to save.');
        setSaving(false);
        return;
      }
      await updateAppointment(appointment.appointment_id, payload);
      setSaved(true);
      setTimeout(() => { onSaved(); }, 800);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  }

  const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zoddle-green';

  return (
    <Modal onClose={onClose}>
      <div className="p-6 w-full max-w-2xl max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-bold text-gray-800">Appointment Details</h2>
          <div className="flex items-center gap-3">
            <CoinBadge appt={appointment} />
            <RefreshCoinsButton appt={appointment} />
            <button onClick={() => setHistoryOpen(true)}
              className="text-xs text-zoddle-blue hover:underline">View history</button>
          </div>
        </div>
        <p className="text-xs text-gray-400 mb-5">{appointment.scheduled_date} · {appointment.scheduled_time}</p>

        {/* Read-only header: customer name, phone, badges, UID */}
        <div className="grid grid-cols-2 gap-x-8 gap-y-3 mb-5 text-sm">
          <div>
            <p className="text-xs font-semibold text-gray-400 mb-0.5">Customer</p>
            <p className="text-gray-800 font-medium">{appointment.customer_name || '—'}</p>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full mt-1 inline-block ${appointment.is_repeat ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
              {appointment.is_repeat ? 'Repeat' : 'New'}
            </span>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-400 mb-0.5">Phone</p>
            <p className="text-gray-800">{appointment.phone || '—'}</p>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-0.5">Referral Code</label>
            <input type="text" value={apptForm.referral_code}
              onChange={e => setApptField('referral_code', e.target.value)}
              className={inputCls} />
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-400 mb-0.5">Source (sheet)</p>
            <p className="text-gray-800">{appointment.source_l2 || '—'}</p>
          </div>
          {appointment.zcp_appointment_uid && (
            <div className="col-span-2">
              <p className="text-xs font-semibold text-gray-400 mb-0.5">Appointment UID</p>
              <p className="text-gray-800 font-mono text-xs">{appointment.zcp_appointment_uid}</p>
            </div>
          )}
        </div>

        {/* Date & Time */}
        <div className="border-t border-gray-100 pt-4 mb-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Date &amp; Time</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Date</label>
              <input type="date" value={apptForm.current_scheduled_date}
                onChange={e => setApptField('current_scheduled_date', e.target.value)}
                className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Time slot</label>
              <input type="text" value={apptForm.current_scheduled_time}
                onChange={e => setApptField('current_scheduled_time', e.target.value)}
                placeholder="e.g. 10:00 AM - 12:00 PM"
                className={inputCls} />
            </div>
          </div>
        </div>

        {/* Order Ops actions */}
        <div className="border-t border-gray-100 pt-4 mb-4">
          <div className={`grid ${canDuplicate ? 'grid-cols-4' : 'grid-cols-3'} gap-3`}>
            <button onClick={() => goToOrderOps('pack')}
              className="bg-zoddle-green text-white text-sm font-semibold py-2 rounded-lg hover:opacity-90 transition">
              Pack
            </button>
            <button onClick={() => goToOrderOps('dispatch')}
              className="bg-zoddle-blue text-white text-sm font-semibold py-2 rounded-lg hover:opacity-90 transition">
              Dispatch
            </button>
            <button onClick={() => goToOrderOps('reverse')}
              className="bg-zoddle-pink text-white text-sm font-semibold py-2 rounded-lg hover:opacity-90 transition">
              Reverse
            </button>
            {canDuplicate && (
              <button onClick={() => setDuplicateOpen(true)}
                className="bg-gray-700 text-white text-sm font-semibold py-2 rounded-lg hover:opacity-90 transition">
                Duplicate
              </button>
            )}
          </div>
        </div>

        {/* Status & Source */}
        <div className="border-t border-gray-100 pt-4 mb-4 space-y-3">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Status &amp; Source</p>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Source</label>
            <select value={apptForm.source_l1} onChange={e => setApptField('source_l1', e.target.value)}
              className={inputCls}>
              {SOURCE_L1_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Operational Status</label>
              <select value={apptForm.operational_status}
                onChange={e => setApptField('operational_status', e.target.value)} className={inputCls}>
                {EDITABLE_OP_STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Customer Status</label>
              <select value={apptForm.customer_status}
                onChange={e => setApptField('customer_status', e.target.value)} className={inputCls}>
                {CUST_STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
          </div>
          {apptForm.customer_status === 'cancelled' && (
            <div className="bg-red-50 border border-red-100 rounded-lg p-3 space-y-2">
              <p className="text-xs font-semibold text-red-700">
                Cancellation reason — sent to the customer as a push notification
              </p>
              <select value={apptForm.cancellation_reason_tag}
                onChange={e => setCancellationReasonTag(e.target.value)}
                className={inputCls}>
                {CANCELLATION_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
              <textarea
                value={apptForm.cancellation_reason_message}
                onChange={e => setApptField('cancellation_reason_message', e.target.value)}
                readOnly={apptForm.cancellation_reason_tag !== '' && apptForm.cancellation_reason_tag !== 'other'}
                rows={3}
                placeholder="Type the message the customer will receive..."
                className={`${inputCls} resize-none ${
                  apptForm.cancellation_reason_tag && apptForm.cancellation_reason_tag !== 'other'
                    ? 'bg-gray-100 text-gray-600'
                    : ''
                }`}
              />
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Notes</label>
            <textarea value={apptForm.notes} onChange={e => setApptField('notes', e.target.value)}
              rows={2} placeholder="Special instructions..."
              className={`${inputCls} resize-none`} />
          </div>
        </div>

        {/* Address */}
        <div className="border-t border-gray-100 pt-4 mb-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Address</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-gray-500 mb-1">Address line 1</label>
              <input type="text" value={apptForm.address_line_1}
                onChange={e => setApptField('address_line_1', e.target.value)}
                className={inputCls} />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-gray-500 mb-1">Address line 2</label>
              <input type="text" value={apptForm.address_line_2}
                onChange={e => setApptField('address_line_2', e.target.value)}
                className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Area</label>
              <input type="text" value={apptForm.area}
                onChange={e => setApptField('area', e.target.value)}
                className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Pincode</label>
              <input type="text" value={apptForm.pincode}
                onChange={e => setApptField('pincode', e.target.value)}
                className={inputCls} />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-gray-500 mb-1">Google Maps link</label>
              <input type="text" value={apptForm.google_maps_link}
                onChange={e => setApptField('google_maps_link', e.target.value)}
                placeholder="https://maps.google.com/..."
                className={inputCls} />
              {apptForm.address_line_1 && (
                <a href={mapsUrl(apptForm.address_line_1)} target="_blank" rel="noreferrer"
                  className="text-xs text-zoddle-blue hover:underline mt-1 inline-block">Open address in Maps ↗</a>
              )}
            </div>
          </div>
        </div>

        {/* What the parent picked on the website (/book-v2) */}
        {appointment.selected_designs && appointment.selected_designs.length > 0 && (
          <div className="border-t border-gray-100 pt-4 mb-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
              Designs the parent picked <span className="normal-case font-normal text-gray-400">· we bring these or similar ones</span>
            </p>
            <SelectedDesigns designs={appointment.selected_designs} childSizes={(appointment.children || []).map(c => c.size).filter(Boolean)} />
          </div>
        )}

        {/* Children */}
        {appointment.children && appointment.children.length > 0 && (
          <div className="border-t border-gray-100 pt-4 mb-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Children</p>
            <div className="space-y-3">
              {appointment.children.map((c) => {
                const f = childrenForm[c.appointment_child_id] || {};
                const gender = c.gender ? (c.gender.charAt(0).toUpperCase() + c.gender.slice(1)) : '—';
                return (
                  <div key={c.appointment_child_id} className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-2"><span className="font-semibold text-gray-700">{gender}</span> · child_id <span className="font-mono">{(c.child_id || '').slice(0, 8)}</span></p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1">Size</label>
                        <select value={f.size}
                          onChange={e => setChildField(c.appointment_child_id, 'size', e.target.value)}
                          className={inputCls}>
                          <option value="">—</option>
                          {zoddleSizes.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1">Brand prefs (child baseline)</label>
                        <input type="text" value={f.brand_pref}
                          onChange={e => setChildField(c.appointment_child_id, 'brand_pref', e.target.value)}
                          placeholder="brand1;brand2"
                          className={inputCls} />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-xs font-semibold text-gray-500 mb-1">Use-case prefs (items)</label>
                        <input type="text" value={f.use_case_pref}
                          onChange={e => setChildField(c.appointment_child_id, 'use_case_pref', e.target.value)}
                          placeholder="partywear;casualwear"
                          className={inputCls} />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-xs font-semibold text-gray-500 mb-1">Style prefs (liked styles)</label>
                        <input type="text" value={f.style_pref}
                          onChange={e => setChildField(c.appointment_child_id, 'style_pref', e.target.value)}
                          className={inputCls} />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-xs font-semibold text-gray-500 mb-1">Avoid tags</label>
                        <input type="text" value={f.avoid_tags}
                          onChange={e => setChildField(c.appointment_child_id, 'avoid_tags', e.target.value)}
                          placeholder="frills;sleeveless"
                          className={inputCls} />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-xs font-semibold text-gray-500 mb-1">Notes (this appointment)</label>
                        <input type="text" value={f.notes}
                          onChange={e => setChildField(c.appointment_child_id, 'notes', e.target.value)}
                          className={inputCls} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {error && <p className="text-xs text-zoddle-pink">{error}</p>}
        {saved && <p className="text-xs text-green-600">✓ Saved</p>}
        <div className="flex gap-3 pt-2 sticky bottom-0 bg-white">
          <button onClick={handleSave} disabled={saving}
            className="flex-1 bg-zoddle-green text-white text-sm font-semibold py-2 rounded-lg hover:opacity-90 disabled:opacity-50 transition">
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          <button onClick={onClose}
            className="flex-1 border border-gray-200 text-gray-600 text-sm font-semibold py-2 rounded-lg hover:bg-gray-50 transition">
            Close
          </button>
        </div>
      </div>
      {historyOpen && (
        <HistoryModal appointment={appointment} onClose={() => setHistoryOpen(false)} />
      )}
      {duplicateOpen && (
        <DuplicateModal appointment={appointment} onClose={() => setDuplicateOpen(false)} onCreated={onSaved} />
      )}
    </Modal>
  );
}

// ── Duplicate Modal ───────────────────────────────────────────────────────
// Copies the appointment (first child only) into a new booking, created
// through the Skipzy app's booking API — the app writes the sheet row and
// sends the appointment into ZOCS.

function DuplicateModal({ appointment, onClose, onCreated }) {
  const ageSizes = useAgeSizes();
  const child = (appointment.children || [])[0] || {};
  const [form, setForm] = useState({
    size: child.size && child.size !== 'Unknown' ? child.size : '',
    gender: child.gender === 'boy' ? 'boy' : 'girl',
    date: appointment.scheduled_date || '',
    time: appointment.scheduled_time || '',
    styles: child.style_pref_override || '',
    pieces: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [created, setCreated] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zoddle-green';
  const labelCls = 'block text-xs font-semibold text-gray-500 mb-1';

  async function handleCreate() {
    if (!form.size || !form.date || !form.time.trim()) { setError('Age, date and time are required.'); return; }
    setSaving(true); setError(null);
    try {
      const res = await duplicateAppointment(appointment.appointment_id, {
        source_child_id: child.appointment_child_id,
        size: form.size, gender: form.gender, date: form.date, time: form.time.trim(),
        styles: form.styles, pieces: form.pieces,
      });
      setCreated(res.zcp_appointment_uid);
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Failed to duplicate');
    } finally { setSaving(false); }
  }

  return (
    <Modal onClose={created ? onCreated : onClose}>
      <div className="p-6 w-full max-w-lg">
        <h2 className="text-lg font-bold text-gray-800 mb-1">Duplicate appointment</h2>
        <p className="text-xs text-gray-400 mb-4">
          Copy of {appointment.zcp_appointment_uid} for {appointment.customer_name || 'this customer'}.
          Booked through the Skipzy app, like any other booking.
        </p>
        {created ? (
          <div className="space-y-4">
            <p className="text-sm text-green-700">
              ✓ Created <span className="font-mono font-semibold">{created}</span>. It shows up in ZOCS within a few seconds. Use this ID when logging its sale or RTO.
            </p>
            <button onClick={onCreated}
              className="w-full bg-zoddle-green text-white text-sm font-semibold py-2 rounded-lg hover:opacity-90 transition">
              Done
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Age</label>
                <select value={form.size} onChange={e => set('size', e.target.value)} className={inputCls}>
                  <option value="">Select age</option>
                  {ageSizes.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Gender</label>
                <select value={form.gender} onChange={e => set('gender', e.target.value)} className={inputCls}>
                  <option value="girl">Girl</option>
                  <option value="boy">Boy</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Date</label>
                <input type="date" value={form.date} onChange={e => set('date', e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Time slot</label>
                <input type="text" value={form.time} onChange={e => set('time', e.target.value)}
                  placeholder="e.g. 2:00 PM - 3:00 PM" className={inputCls} />
              </div>
            </div>
            <div>
              <label className={labelCls}>Styles</label>
              <textarea rows={2} value={form.styles} onChange={e => set('styles', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Pieces they need (optional)</label>
              <input type="text" value={form.pieces} onChange={e => set('pieces', e.target.value)}
                placeholder="Zoddle barcodes, comma separated" className={inputCls} />
              <p className="text-xs text-gray-400 mt-1">Added to the start of Styles.</p>
            </div>
            {error && <p className="text-xs text-zoddle-pink">{error}</p>}
            <div className="flex gap-3 pt-1">
              <button onClick={handleCreate} disabled={saving}
                className="flex-1 bg-zoddle-green text-white text-sm font-semibold py-2 rounded-lg hover:opacity-90 disabled:opacity-50 transition">
                {saving ? 'Creating...' : 'Create duplicate'}
              </button>
              <button onClick={onClose}
                className="flex-1 border border-gray-200 text-gray-600 text-sm font-semibold py-2 rounded-lg hover:bg-gray-50 transition">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ── History Modal ─────────────────────────────────────────────────────────

function HistoryModal({ appointment, onClose }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try { setRows(await getAppointmentChangeLog(appointment.appointment_id)); }
      catch (e) { setError(e.response?.data?.detail || 'Failed to load history.'); }
    })();
  }, [appointment.appointment_id]);

  return (
    <Modal onClose={onClose}>
      <div className="p-6 w-full max-w-3xl max-h-[80vh] overflow-y-auto">
        <h2 className="text-lg font-bold text-gray-800 mb-3">Appointment change history</h2>
        {error && <p className="text-xs text-zoddle-pink mb-3">{error}</p>}
        {rows === null && !error && <p className="text-xs text-gray-400">Loading...</p>}
        {rows && rows.length === 0 && <p className="text-xs text-gray-400">No changes recorded yet.</p>}
        {rows && rows.length > 0 && (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-400 text-left border-b border-gray-100">
                <th className="py-2 pr-3 font-semibold">When</th>
                <th className="py-2 pr-3 font-semibold">Who</th>
                <th className="py-2 pr-3 font-semibold">Field</th>
                <th className="py-2 pr-3 font-semibold">Old</th>
                <th className="py-2 font-semibold">New</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.change_id} className="border-b border-gray-50 align-top">
                  <td className="py-2 pr-3 text-gray-600 whitespace-nowrap">{r.changed_at.slice(0, 19).replace('T', ' ')}</td>
                  <td className="py-2 pr-3 text-gray-700">{r.changed_by_name}</td>
                  <td className="py-2 pr-3 text-gray-700">{describeField(r.field_changed, appointment)}</td>
                  <td className="py-2 pr-3 text-gray-500 break-all">{r.old_value || '—'}</td>
                  <td className="py-2 text-gray-800 break-all">{r.new_value || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="pt-3">
          <button onClick={onClose}
            className="w-full border border-gray-200 text-gray-600 text-sm font-semibold py-2 rounded-lg hover:bg-gray-50 transition">
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ── Error Correction Modal ────────────────────────────────────────────────

function ErrorCorrectionModal({ err, onClose, onResolved }) {
  function parseErrorFields(reason) {
    const fields = [];
    if (reason.includes('phone') || reason.includes('Missing phone')) fields.push({ key: 'raw_phone', label: 'Phone Number' });
    if (reason.includes('date')) fields.push({ key: 'raw_date', label: 'Date' });
    if (reason.includes('time')) fields.push({ key: 'raw_time', label: 'Time' });
    if (reason.includes('gender')) fields.push({ key: 'raw_gender', label: 'Gender' });
    if (reason.includes('age') || reason.includes('size')) fields.push({ key: 'raw_age', label: 'Age / Size' });
    if (fields.length === 0) fields.push(
      { key: 'raw_phone', label: 'Phone' },
      { key: 'raw_date', label: 'Date' },
      { key: 'raw_time', label: 'Time' },
      { key: 'raw_gender', label: 'Gender' },
      { key: 'raw_age', label: 'Age / Size' },
    );
    return fields;
  }

  const errorFields = parseErrorFields(err.error_reason);
  const [form, setForm] = useState(() => {
    const init = {};
    errorFields.forEach(f => { init[f.key] = err[f.key] || ''; });
    return init;
  });
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  async function handleSave() {
    setSaving(true); setResult(null);
    try {
      const res = await correctError(err.error_id, form);
      setResult(res);
      if (res.resolved) setTimeout(() => { onResolved(); }, 1000);
    } catch (e) {
      setResult({ resolved: false, remaining_errors: e.response?.data?.detail || 'Unknown error' });
    } finally { setSaving(false); }
  }

  return (
    <Modal onClose={onClose}>
      <div className="p-6 w-full max-w-md">
        <h2 className="text-lg font-bold text-gray-800 mb-1">Fix Import Error</h2>
        <p className="text-xs text-gray-400 mb-2">{err.raw_parent_name} · {err.raw_phone}</p>
        <div className="bg-red-50 border border-red-100 rounded-lg px-3 py-2 mb-5">
          <p className="text-xs text-zoddle-pink font-medium">{err.error_reason}</p>
        </div>
        <div className="space-y-3">
          {errorFields.map(f => (
            <div key={f.key}>
              <label className="block text-xs font-semibold text-gray-500 mb-1">{f.label}</label>
              <div className="flex gap-2 items-center">
                <span className="text-xs text-gray-400 bg-gray-50 border border-gray-200 rounded px-2 py-1.5 flex-1 truncate">
                  {err[f.key] || '(empty)'}
                </span>
                <span className="text-gray-300 text-xs">→</span>
                <input type="text" value={form[f.key] || ''}
                  onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                  placeholder="Corrected value"
                  className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-zoddle-green" />
              </div>
            </div>
          ))}
        </div>
        {result && (
          <div className={`mt-4 px-3 py-2 rounded-lg text-xs font-medium ${result.resolved ? 'bg-green-50 text-green-700' : 'bg-red-50 text-zoddle-pink'}`}>
            {result.resolved ? '✓ Resolved — appointment created' : `Still failing: ${result.remaining_errors}`}
          </div>
        )}
        <div className="flex gap-3 mt-5">
          <button onClick={handleSave} disabled={saving}
            className="flex-1 bg-zoddle-green text-white text-sm font-semibold py-2 rounded-lg hover:opacity-90 disabled:opacity-50 transition">
            {saving ? 'Saving...' : 'Apply & Retry'}
          </button>
          <button onClick={onClose}
            className="flex-1 border border-gray-200 text-gray-600 text-sm font-semibold py-2 rounded-lg hover:bg-gray-50 transition">
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ── Today board (colour-coded cards) ──────────────────────────────────────

const LIVE_STATUSES = new Set(['scheduled', 'pack_assigned']); // still need dispatch
const TODAY_COLOUR_STYLES = {
  red:    'bg-red-50 border-red-300',
  yellow: 'bg-amber-50 border-amber-300',
  green:  'bg-green-50 border-green-300',
  grey:   'bg-gray-100 border-gray-200 opacity-70',
};
const TODAY_COLOUR_ORDER = { red: 0, yellow: 1, green: 2, grey: 3 };

// Today board sections. Grouping only — card colours are unchanged. A still-
// pending "Message sent" booking is Inactive rather than Upcoming; once it has
// moved on (completed, cancelled, dispatched…) it goes where any booking with
// that order status goes. An order status not listed here lands in In progress.
const TODAY_SECTIONS = [
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'inactive', label: 'Inactive' },
  { key: 'completed', label: 'Completed' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'cancelled', label: 'Cancelled' },
];
function todaySection(appt) {
  const s = appt.operational_status;
  if (LIVE_STATUSES.has(s)) return appt.customer_status === 'message_sent' ? 'inactive' : 'upcoming';
  if (['completed', 'rto', 'dol'].includes(s)) return 'completed';
  if (s === 'cancelled') return 'cancelled';
  return 'in_progress';
}

// Parse the slot's START time (e.g. "10:00 AM - 11:00 AM" -> ms on that date).
function parseSlotStart(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  const parts = String(timeStr).split(/[-–]/);
  const startStr = parts[0].trim();
  const endStr = (parts[1] || '').trim();
  const m = startStr.match(/(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?/i);
  if (!m) return null;
  let h = parseInt(m[1], 10); const min = parseInt(m[2] || '0', 10);
  let period = (m[3] || '').toUpperCase();
  // Glitch: "11:00 PM - 12:00 PM" actually means 11 AM.
  if (h === 11 && period === 'PM' && /12(:00)?\s*PM/i.test(endStr)) period = 'AM';
  if (period === 'PM' && h !== 12) h += 12;
  else if (period === 'AM' && h === 12) h = 0;
  const d = new Date(`${dateStr}T00:00:00`);
  if (isNaN(d.getTime())) return null;
  d.setHours(h, min, 0, 0);
  return d.getTime();
}

// Compact slot label for messages: "2:00 PM - 3:00 PM" -> "2-3PM".
function formatSlotCompact(timeStr) {
  if (!timeStr) return '';
  const parts = String(timeStr).split(/[-–]/);
  const parse = s => {
    const m = String(s).trim().match(/(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?/i);
    return m ? { h: parseInt(m[1], 10), min: parseInt(m[2] || '0', 10), p: (m[3] || '').toUpperCase() } : null;
  };
  const a = parse(parts[0]); const b = parse(parts[1] || '');
  if (!a) return String(timeStr).trim();
  if (a.h === 11 && a.p === 'PM' && b && b.h === 12 && b.p === 'PM') a.p = 'AM'; // 11PM-12PM glitch = 11AM
  const fmt = x => `${x.h}${x.min ? ':' + String(x.min).padStart(2, '0') : ''}`;
  if (!b) return `${fmt(a)}${a.p}`;
  if (a.p && b.p && a.p === b.p) return `${fmt(a)}-${fmt(b)}${b.p}`;
  return `${fmt(a)}${a.p}-${fmt(b)}${b.p}`;
}

// "2-3Y" boy -> "2-3 Years (Boy)"; multiple children joined with ", ".
function formatReminderSize(children) {
  return (children || []).map(c => {
    const raw = (c.size || '').trim();
    const unit = raw.endsWith('Y') ? 'Years' : raw.endsWith('M') ? 'Months' : '';
    const num = unit ? raw.slice(0, -1) : raw;
    const gender = c.gender === 'boy' ? 'Boy' : c.gender === 'girl' ? 'Girl' : '';
    const sizePart = num && unit ? `${num} ${unit}` : raw;
    return gender ? `${sizePart} (${gender})` : sizePart;
  }).filter(Boolean).join(', ');
}

function buildReminderMessage(a) {
  return `Hi! This is Prakhar from Skipzy. We tried calling you to remind you of your kids shopping from home session tomorrow. We will bring 25+ clothes based on your preferences. Please see the details and confirm below:\n\nTime: ${formatSlotCompact(a.scheduled_time)}\nSize: ${formatReminderSize(a.children)}\n\nI hope you have a great experience shopping with us tomorrow! Please do confirm your availability when you can. Thanks!`;
}

function cardColour(appt, startMs, nowMs) {
  if (!LIVE_STATUSES.has(appt.operational_status)) return 'grey'; // dispatched/cancelled/done
  if (startMs == null) return 'green';
  const hrs = (startMs - nowMs) / 3600000;
  if (hrs <= 1) return 'red';
  if (hrs <= 3) return 'yellow';
  return 'green';
}

// "created dd/mm" from an ISO/naive datetime string. Sliced (not Date-parsed)
// to avoid timezone shifts on the naive-UTC created_at value.
function createdLabel(iso) {
  if (!iso) return null;
  const p = String(iso).slice(0, 10).split('-'); // [YYYY, MM, DD]
  return p.length === 3 ? `created ${p[2]}/${p[1]}` : null;
}

// Live coin balance from the customer app (same preview call the ZOCS Sync tab uses; it writes nothing).
// Admin only — that endpoint is admin-gated. Shown only when the customer has an app account.
// Fetched ONLY when the admin clicks "Refresh coins" in an appointment's modal — cached per phone for the session.
const coinCache = new Map();           // phone10 -> coin balance (null = no app account)
const coinListeners = new Set();
const phone10Of = (appt) => (appt.phone || '').replace(/\D/g, '').slice(-10);

async function refreshCoinsFor(appt) {
  const phone10 = phone10Of(appt);
  if (!IS_ADMIN() || phone10.length !== 10 || !appt.zcp_appointment_uid) return;
  try {
    const p = await previewZocsSync(appt.zcp_appointment_uid, phone10, appt.scheduled_date);
    coinCache.set(phone10, p && p.has_app_account ? (p.coin_balance ?? 0) : null);
    coinListeners.forEach((fn) => fn());
  } catch { /* leave the previous value */ }
}

function useCoins(appt) {
  const [, bump] = useState(0);
  useEffect(() => {
    const fn = () => bump((n) => n + 1);
    coinListeners.add(fn);
    return () => coinListeners.delete(fn);
  }, []);
  return coinCache.get(phone10Of(appt));
}

function CoinBadge({ appt }) {
  const coins = useCoins(appt);
  if (coins == null) return null;
  return <span className="ml-1.5 text-xs font-medium text-amber-700 bg-amber-100 rounded px-1.5 py-0.5" title="Coin balance (last refreshed)">🪙 {coins}</span>;
}

// Refreshes just this one appointment's phone number.
function RefreshCoinsButton({ appt }) {
  const [busy, setBusy] = useState(false);
  if (!IS_ADMIN()) return null;
  return (
    <button type="button" disabled={busy}
      onClick={async (e) => { e.stopPropagation(); setBusy(true); await refreshCoinsFor(appt); setBusy(false); }}
      className="ml-2 text-xs font-semibold text-amber-700 bg-amber-100 rounded px-2 py-0.5 hover:opacity-80 disabled:opacity-50">
      {busy ? '…' : '🪙 Refresh coins'}
    </button>
  );
}

function TodayCard({ appt, nowMs, onSelect, riderName }) {
  const startMs = parseSlotStart(appt.scheduled_date, appt.scheduled_time);
  const colour = cardColour(appt, startMs, nowMs);
  const created = createdLabel(appt.created_at);
  return (
    <div onClick={() => onSelect(appt)}
      className={`border-2 rounded-xl p-3 cursor-pointer hover:shadow-md transition flex flex-col gap-2 ${TODAY_COLOUR_STYLES[colour]}`}>
      <div className="flex items-center justify-between">
        <p className="text-sm font-bold text-gray-800">{appt.scheduled_time || '—'}<FreeShoeIcon code={appt.referral_code} /></p>
        {created && <span className="text-xs italic text-gray-400">{created}</span>}
      </div>
      <div>
        <p className="text-sm font-semibold text-gray-800">
          {appt.customer_name || '—'}
          <CoinBadge appt={appt} />
        </p>
        <p className="text-xs text-gray-500">{appt.phone || '—'}</p>
      </div>
      {appt.address_line_1 && (
        <p className="text-xs text-gray-500 line-clamp-2">{appt.address_line_1}{appt.area ? `, ${appt.area}` : ''}</p>
      )}
      <div className="border-t border-black/5 pt-2 space-y-1.5">
        {(appt.children || []).map((c, i) => (
          <div key={i} className="text-xs">
            <p className="font-semibold text-gray-700">
              {riderName && i === 0
                ? `${c.size} - ${(c.gender || '').replace(/^./, ch => ch.toUpperCase())} (${riderName})`
                : `${c.size} · ${c.gender}`}
            </p>
            {c.brand_pref_override && <p className="text-gray-500">Brand: {c.brand_pref_override}</p>}
            {c.use_case_pref_override && <p className="text-gray-500">Items: {c.use_case_pref_override}</p>}
            {c.style_pref_override && <p className="text-gray-500">Styles: {c.style_pref_override}</p>}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mt-auto pt-1">
        <Badge value={appt.operational_status} colourMap={OP_COLOURS} />
        <Badge value={appt.customer_status} colourMap={CUST_COLOURS} />
      </div>
    </div>
  );
}

function TodayBoard({ appointments, nowMs, onSelect }) {
  const cards = useMemo(() => (
    appointments
      .filter(a => !['junk', 'duplicate'].includes(a.customer_status))
      .map(a => {
        const startMs = parseSlotStart(a.scheduled_date, a.scheduled_time);
        return { a, colour: cardColour(a, startMs, nowMs), start: startMs == null ? Infinity : startMs };
      })
      .sort((x, y) => (TODAY_COLOUR_ORDER[x.colour] - TODAY_COLOUR_ORDER[y.colour]) || (x.start - y.start))
  ), [appointments, nowMs]);

  const [open, setOpen] = useState({ upcoming: true });
  const [riders, setRiders] = useState({});        // appointment_id -> rider name
  const [riderState, setRiderState] = useState({}); // section key -> 'loading' | message
  const sections = useMemo(() => {
    const by = Object.fromEntries(TODAY_SECTIONS.map(s => [s.key, []]));
    cards.forEach(c => by[todaySection(c.a)].push(c));
    return TODAY_SECTIONS.map(s => ({ ...s, cards: by[s.key] })).filter(s => s.cards.length);
  }, [cards]);

  // Rider names load only on request (no rider-backend call on page load).
  // In progress: live from the rider app, via the Rider Assignment board's
  // endpoint. Completed: the rider ("executive") recorded on the order.
  async function loadRiders(section) {
    setOpen(o => ({ ...o, [section.key]: true }));
    setRiderState(st => ({ ...st, [section.key]: 'loading' }));
    try {
      const found = {};
      if (section.key === 'in_progress') {
        (await getRiderAssignmentJobs()).forEach(r => { if (r.rider_name) found[r.appointment_id] = r.rider_name; });
      } else {
        const day = d => new Date(nowMs + d * 86400000).toLocaleDateString('en-CA');
        const res = await listPastOrders({ date_from: day(-1), date_to: day(1), limit: 2000 });
        const byUid = {};
        (res.orders || []).forEach(o => {
          if (o.appointment_uid && o.executive) byUid[o.appointment_uid.toLowerCase()] = o.executive;
        });
        section.cards.forEach(({ a }) => {
          const name = byUid[(a.zcp_appointment_uid || '').toLowerCase()];
          if (name) found[a.appointment_id] = name;
        });
      }
      setRiders(r => ({ ...r, ...found }));
      const n = section.cards.filter(({ a }) => found[a.appointment_id]).length;
      setRiderState(st => ({ ...st, [section.key]: `${n} of ${section.cards.length} with a rider` }));
    } catch (e) {
      const msg = e.response?.status === 403 ? 'Rider details need core team access' : 'Could not load rider details';
      setRiderState(st => ({ ...st, [section.key]: msg }));
    }
  }

  if (cards.length === 0) return null;
  const todayStr = new Date(nowMs).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
  return (
    <div className="mb-8">
      <div className="flex items-center gap-3 mb-3">
        <h2 className="text-lg font-black text-gray-800">Today</h2>
        <span className="text-xs text-gray-400">{todayStr} · {cards.length} appointments</span>
      </div>
      {sections.map(s => (
        <div key={s.key} className="mb-4">
          <div className="flex items-center gap-3 mb-2">
            <button onClick={() => setOpen(o => ({ ...o, [s.key]: !o[s.key] }))}
              className="flex items-center gap-2 text-sm font-bold text-gray-700 hover:text-gray-900">
              <span className="inline-block w-3 text-gray-400">{open[s.key] ? '▾' : '▸'}</span>
              {s.label} <span className="text-xs font-normal text-gray-400">{s.cards.length}</span>
            </button>
            {(s.key === 'in_progress' || s.key === 'completed') && (
              <>
                <button onClick={() => loadRiders(s)} disabled={riderState[s.key] === 'loading'}
                  className="text-xs font-semibold px-2.5 py-1 rounded-lg border border-gray-200 text-gray-600 hover:border-gray-400 disabled:opacity-50">
                  {riderState[s.key] === 'loading' ? 'Loading…' : 'Rider Details'}
                </button>
                {riderState[s.key] && riderState[s.key] !== 'loading' && (
                  <span className="text-xs text-gray-400">{riderState[s.key]}</span>
                )}
              </>
            )}
          </div>
          {open[s.key] && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {s.cards.map(({ a }) => <TodayCard key={a.appointment_id} appt={a} nowMs={nowMs} onSelect={onSelect} riderName={riders[a.appointment_id]} />)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Admin View — mirrors Order Ops → Packing, with status actions + copy icons ──
const AdminCopyIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none"
       viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 012-2h10" />
  </svg>
);

const CUST_STATUS_LABEL = {
  booked: 'Booked', message_sent: 'Message Sent',
  confirmed_on_delivery_day: 'Confirmed (Delivery Day)', confirmed_on_call_or_text: 'Confirmed',
  cancelled: 'Cancelled', duplicate: 'Duplicate', delivered: 'Delivered', rto: 'RTO',
  junk: 'Junk', customer_not_on_whatsapp: 'Not on WhatsApp',
};
function prettyStatus(s) {
  if (!s) return '—';
  return CUST_STATUS_LABEL[s] || s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}
function custTone(s) {
  if (!s) return 'gray';
  if (s.startsWith('confirmed') || s === 'delivered') return 'green';
  if (s === 'message_sent') return 'blue';
  if (['cancelled', 'junk', 'duplicate', 'rto'].includes(s)) return 'pink';
  return 'gray';
}
function StatusPill({ tone = 'gray', children }) {
  const tones = {
    gray: 'bg-gray-100 text-gray-600', green: 'bg-green-100 text-green-700',
    blue: 'bg-blue-50 text-zoddle-blue', pink: 'bg-pink-50 text-zoddle-pink',
  };
  return <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full ${tones[tone]}`}>{children}</span>;
}

function AppointmentsAdminView({ onBack }) {
  const today = new Date().toLocaleDateString('en-CA');
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [copiedMsgId, setCopiedMsgId] = useState(null);
  const [copiedPhoneId, setCopiedPhoneId] = useState(null);
  const [actionState, setActionState] = useState({});   // appointment_id -> feedback label
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      setAppts(await getPackableAppointments(params));
    } catch (e) { /* leave list as-is */ }
    finally { setLoading(false); }
  }, [dateFrom, dateTo]);
  useEffect(() => { load(); }, [load]);

  async function copyMsg(a) {
    try { await navigator.clipboard.writeText(buildReminderMessage(a)); setCopiedMsgId(a.appointment_id); setTimeout(() => setCopiedMsgId(null), 1200); } catch {}
  }
  async function copyPhoneNum(a) {
    try { await navigator.clipboard.writeText(a.phone || ''); setCopiedPhoneId(a.appointment_id); setTimeout(() => setCopiedPhoneId(null), 1200); } catch {}
  }
  async function act(a, kind) {
    if (kind === 'cancel' && !window.confirm(`Cancel appointment for ${a.customer_name || a.phone}? This sets both customer and operational status to cancelled.`)) return;
    setBusyId(a.appointment_id);
    try {
      if (kind === 'message_sent') await updateAppointmentStatus(a.appointment_id, { customer_status: 'message_sent' });
      else if (kind === 'confirm') await updateAppointmentStatus(a.appointment_id, { customer_status: 'confirmed_on_delivery_day' });
      else if (kind === 'cancel') await updateAppointmentStatus(a.appointment_id, { customer_status: 'cancelled', operational_status: 'cancelled' });
      if (kind === 'cancel') {
        setAppts(prev => prev.filter(x => x.appointment_id !== a.appointment_id));
      } else {
        const cs = kind === 'message_sent' ? 'message_sent' : 'confirmed_on_delivery_day';
        setAppts(prev => prev.map(x => x.appointment_id === a.appointment_id ? { ...x, customer_status: cs } : x));
        setActionState(prev => ({ ...prev, [a.appointment_id]: kind === 'message_sent' ? 'Message sent ✓' : 'Confirmed ✓' }));
      }
    } catch (e) {
      alert(e?.response?.data?.detail || 'Could not update status');
    } finally { setBusyId(null); }
  }

  return (
    <div className="p-6 max-w-screen-xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="text-sm text-gray-400 hover:text-zoddle-green">← Back</button>
          <div>
            <h1 className="text-2xl font-black text-gray-800">Appointments · Admin</h1>
            <p className="text-sm text-gray-400 mt-0.5">Message / confirm / cancel upcoming appointments</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm" />
          <span className="text-gray-400 text-sm">to</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm" />
          <button onClick={load} className="bg-gray-800 text-white text-sm px-3 py-2 rounded-lg">Apply</button>
        </div>
      </div>

      <p className="text-xs text-gray-400 mb-3">{appts.length} appointments</p>
      {loading ? <div className="text-center py-16 text-gray-400 text-sm">Loading...</div>
      : appts.length === 0 ? <div className="text-center py-16 text-gray-400 text-sm">No appointments</div>
      : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Date & Time</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Customer</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">UID</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Children</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {appts.map((a, i) => (
                <tr key={a.appointment_id} className={`border-b border-gray-100 ${i % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                  <td className="px-4 py-3">
                    <p className="font-semibold text-gray-800 whitespace-nowrap">{a.scheduled_date}</p>
                    <p className="text-xs text-gray-400 whitespace-nowrap">{a.scheduled_time}</p>
                  </td>
                  <td className="px-4 py-3 w-44">
                    <div className="flex items-center gap-1.5">
                      <p className="font-medium text-gray-800">{a.customer_name || '—'}</p>
                      <button onClick={() => copyMsg(a)} title="Copy reminder message" className="text-gray-400 hover:text-zoddle-green shrink-0">
                        {copiedMsgId === a.appointment_id ? <span className="text-xs font-semibold text-zoddle-green">✓</span> : <AdminCopyIcon />}
                      </button>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <p className="text-xs text-gray-400">{a.phone}</p>
                      {a.phone && (
                        <button onClick={() => copyPhoneNum(a)} title="Copy phone number" className="text-gray-400 hover:text-zoddle-green shrink-0">
                          {copiedPhoneId === a.appointment_id ? <span className="text-[10px] font-semibold text-zoddle-green">✓</span> : <AdminCopyIcon />}
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3"><span className="font-mono text-xs text-gray-600">{a.zcp_appointment_uid || '—'}</span></td>
                  <td className="px-4 py-3">
                    {a.children?.map((c, ci) => <p key={ci} className="text-xs text-gray-600">{c.size} | {c.gender}</p>)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1 items-start">
                      <StatusPill tone={custTone(a.customer_status)}>{prettyStatus(a.customer_status)}</StatusPill>
                      <StatusPill tone="gray">{prettyStatus(a.operational_status)}</StatusPill>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-3 items-center">
                      <button disabled={busyId === a.appointment_id} onClick={() => act(a, 'message_sent')}
                        className="text-xs text-zoddle-blue font-semibold hover:underline disabled:opacity-40">Message Sent</button>
                      <button disabled={busyId === a.appointment_id} onClick={() => act(a, 'confirm')}
                        className="text-xs text-zoddle-green font-semibold hover:underline disabled:opacity-40">Confirm</button>
                      <button disabled={busyId === a.appointment_id} onClick={() => act(a, 'cancel')}
                        className="text-xs text-zoddle-pink font-semibold hover:underline disabled:opacity-40">Cancel</button>
                      {actionState[a.appointment_id] && <span className="text-[10px] font-semibold text-zoddle-green ml-1">{actionState[a.appointment_id]}</span>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main Screen ───────────────────────────────────────────────────────────

export default function Appointments() {
  const [tab, setTab] = useState('appointments');
  const [appointments, setAppointments] = useState([]);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [quickSyncing, setQuickSyncing] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [newBookingOpen, setNewBookingOpen] = useState(false);
  const [editingError, setEditingError] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [retryResult, setRetryResult] = useState(null);
  const [todayAppts, setTodayAppts] = useState([]);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [adminView, setAdminView] = useState(false);

  // Local date (en-CA = YYYY-MM-DD); toISOString would give the UTC date.
  const today = new Date().toLocaleDateString('en-CA');
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  const { navData } = useNavigation();

  // ⌘K deep link: navigate('appointments', { search }) — search every date, not just today.
  useEffect(() => {
    if (!navData?.search) return;
    setSearch(navData.search);
    setDateFrom(''); setDateTo('');
  }, [navData]);
  const [showCancelled, setShowCancelled] = useState(true);
  const [copiedAddrId, setCopiedAddrId] = useState(null);
  const [copiedMsgId, setCopiedMsgId] = useState(null);
  const [copiedPhoneId, setCopiedPhoneId] = useState(null);
  const [prefPopover, setPrefPopover] = useState(null); // { appt, top, left, flip }

  function openPrefPopover(a, el) {
    const r = el.getBoundingClientRect();
    const flip = r.bottom + 200 > window.innerHeight;
    setPrefPopover({
      appt: a,
      top: flip ? r.top - 8 : r.bottom + 8,
      left: Math.max(8, r.left),
      flip,
    });
  }

  useEffect(() => {
    if (!prefPopover) return;
    const close = () => setPrefPopover(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [prefPopover]);

  async function copyPhone(a) {
    try {
      await navigator.clipboard.writeText(a.phone || '');
      setCopiedPhoneId(a.appointment_id);
      setTimeout(() => setCopiedPhoneId(null), 2000);
    } catch (e) { alert('Could not copy to clipboard'); }
  }

  async function copyReminder(a) {
    try {
      await navigator.clipboard.writeText(buildReminderMessage(a));
      setCopiedMsgId(a.appointment_id);
      setTimeout(() => setCopiedMsgId(null), 2000);
    } catch (e) { alert('Could not copy to clipboard'); }
  }

  async function copyAddress(a) {
    const text = [a.address_line_1, a.address_line_2].filter(Boolean).join(', ');
    try {
      await navigator.clipboard.writeText(text);
      setCopiedAddrId(a.appointment_id);
      setTimeout(() => setCopiedAddrId(null), 2000);
    } catch (e) { alert('Could not copy to clipboard'); }
  }

  const loadAppointments = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (filterStatus) params.operational_status = filterStatus;
      if (search) params.search = search;
      if (showAll) params.show_all = true;
      const data = await getAppointments(params);
      setAppointments(data);
    } catch (e) {
      console.error('Failed to load appointments', e);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, filterStatus, search, showAll]);

  const loadErrors = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getErrors();
      setErrors(data);
    } catch (e) {
      console.error('Failed to load errors', e);
    } finally {
      setLoading(false);
    }
  }, []);

  // Today board fetches today's appointments independently of the list filters.
  const loadToday = useCallback(async () => {
    try {
      const data = await getAppointments({ date_from: today, date_to: today, show_all: true });
      setTodayAppts(data);
    } catch (e) { console.error('Failed to load today', e); }
  }, [today]);

  useEffect(() => {
    if (tab === 'appointments') { loadAppointments(); loadToday(); }
    if (tab === 'errors') loadErrors();
  }, [tab, loadAppointments, loadErrors, loadToday]);

  // Re-evaluate card colours every minute so an open screen stays current.
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);

  async function handleSyncNew() {
    setSyncing(true); setSyncResult(null);
    try {
      const res = await syncSheets();
      setSyncResult({ success: true, label: 'Sync New', ...res });
      loadAppointments(); loadErrors(); loadToday();
    } catch (e) {
      setSyncResult({ success: false, label: 'Sync New', message: e.response?.data?.detail || 'Sync failed' });
    } finally { setSyncing(false); }
  }

  async function handleQuickSync() {
    setQuickSyncing(true); setSyncResult(null);
    try {
      const res = await quickSyncSheets();
      setSyncResult({ success: true, label: 'Quick Sync', quick: true, ...res });
      loadAppointments(); loadErrors(); loadToday();
    } catch (e) {
      setSyncResult({ success: false, label: 'Quick Sync', message: e.response?.data?.detail || 'Quick Sync failed' });
    } finally { setQuickSyncing(false); }
  }

  async function handleResyncAll() {
    setResyncing(true); setSyncResult(null);
    try {
      const res = await resyncAll();
      setSyncResult({ success: true, label: 'Resync All', ...res });
      loadAppointments(); loadErrors(); loadToday();
    } catch (e) {
      setSyncResult({ success: false, label: 'Resync All', message: e.response?.data?.detail || 'Resync failed' });
    } finally { setResyncing(false); }
  }

  async function handleRetryAll() {
    setRetrying(true); setRetryResult(null);
    try {
      const res = await retryAllErrors();
      setRetryResult(res);
      loadErrors();
    } catch (e) {
      setRetryResult({ message: e.response?.data?.detail || 'Retry failed' });
    } finally { setRetrying(false); }
  }

  const errorCount = errors.length;
  const TABS = [
    { key: 'appointments', label: 'Appointments' },
    { key: 'errors', label: errorCount > 0 ? `Errors (${errorCount})` : 'Errors' },
  ];

  // Table-level filter: when "Cancelled orders" is unchecked, drop any row whose
  // customer OR operational status is cancelled. Per-row, so a duplicate phone
  // with one non-cancelled appointment still shows.
  const visibleAppointments = showCancelled
    ? appointments
    : appointments.filter(a => a.customer_status !== 'cancelled' && a.operational_status !== 'cancelled');

  if (adminView) return <AppointmentsAdminView onBack={() => setAdminView(false)} />;

  return (
    <div className="p-6 max-w-screen-xl mx-auto">

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-black text-gray-800">Appointments</h1>
          <p className="text-sm text-gray-400 mt-0.5">Synced from Google Sheets</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap justify-end">
          {IS_CORE_TEAM() && (
            <button onClick={() => setNewBookingOpen(true)}
              className="bg-zoddle-green text-white text-sm font-semibold px-4 py-2 rounded-lg hover:opacity-90 transition">
              + New booking
            </button>
          )}
          {IS_ADMIN() && (
            <button onClick={() => setAdminView(true)}
              className="bg-gray-800 text-white text-sm font-semibold px-4 py-2 rounded-lg hover:opacity-90 transition"
              title="Admin: message / confirm / cancel appointments">
              ⚙ Admin View
            </button>
          )}
          {syncResult && (
            <div className={`text-xs font-medium px-3 py-1.5 rounded-lg ${!syncResult.success ? 'bg-red-50 text-zoddle-pink' : syncResult.window_exhausted ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700'}`}>
              {!syncResult.success
                ? syncResult.message
                : syncResult.quick
                  ? ('✓ ' + syncResult.message)
                  : ('✓ ' + syncResult.label + ': ' + (syncResult.imported ?? '') + ' imported' + (syncResult.updated != null ? ' · ' + syncResult.updated + ' updated' : '') + ' · ' + syncResult.errors + ' errors')}
            </div>
          )}
          <button onClick={handleQuickSync} disabled={syncing || quickSyncing || resyncing}
            className="bg-zoddle-blue text-white text-sm font-semibold px-4 py-2 rounded-lg hover:opacity-90 disabled:opacity-50 transition flex items-center gap-2"
            title="Fast: checks only the last 50 rows of the sheet">
            {quickSyncing ? <><svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>Quick…</> : '⚡ Quick Sync'}
          </button>
          <button onClick={handleSyncNew} disabled={syncing || quickSyncing || resyncing}
            className="bg-zoddle-green text-white text-sm font-semibold px-4 py-2 rounded-lg hover:opacity-90 disabled:opacity-50 transition flex items-center gap-2">
            {syncing ? <><svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>Syncing...</> : '↻ Sync New'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-6 border-b border-gray-200 mb-6">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`pb-3 text-sm font-semibold border-b-2 transition-colors ${tab === t.key ? 'border-zoddle-green text-zoddle-green' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Appointments Tab */}
      {tab === 'appointments' && (
        <div>
          <TodayBoard appointments={todayAppts} nowMs={nowMs} onSelect={setSelectedAppointment} />

          <div className="flex flex-wrap gap-3 mb-4">
            <input type="text" placeholder="Search name or phone..." value={search}
              onChange={e => setSearch(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zoddle-green w-52" />
            <div className="flex items-center gap-2">
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zoddle-green" />
              <span className="text-gray-400 text-sm">to</span>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zoddle-green" />
            </div>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zoddle-green">
              <option value="">All statuses</option>
              {OP_STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
            </select>
            <button onClick={loadAppointments}
              className="border border-gray-200 text-gray-600 text-sm font-medium px-4 py-2 rounded-lg hover:bg-gray-50 transition">
              Apply
            </button>
            <button
              onClick={() => setShowAll(v => !v)}
              className={`text-xs font-semibold px-3 py-2 rounded-lg border transition ${showAll ? 'bg-gray-700 text-white border-gray-700' : 'border-gray-200 text-gray-600 hover:border-gray-400'}`}>
              {showAll ? 'Hiding cancelled/junk' : 'Show all'}
            </button>
            {(dateFrom !== today || dateTo !== today || filterStatus || search) && (
              <button onClick={() => { setDateFrom(today); setDateTo(today); setFilterStatus(''); setSearch(''); }}
                className="text-xs text-gray-400 hover:text-zoddle-pink transition">Clear</button>
            )}
            <span className="ml-auto text-xs text-gray-400 self-center">{appointments.length} appointments</span>
          </div>

          {!loading && appointments.length > 0 && (
            <div className="flex items-center justify-between mb-2">
              <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none">
                <input type="checkbox" checked={showCancelled}
                  onChange={e => setShowCancelled(e.target.checked)}
                  className="rounded border-gray-300 text-zoddle-green focus:ring-zoddle-green" />
                Cancelled orders
              </label>
              <span className="text-xs text-gray-400">{visibleAppointments.length} shown</span>
            </div>
          )}
          {loading ? (
            <div className="text-center py-16 text-gray-400 text-sm">Loading...</div>
          ) : visibleAppointments.length === 0 ? (
            <div className="text-center py-16 text-gray-400 text-sm">No appointments found</div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 w-32">Date & Time</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 w-40">Customer</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 w-32">Address</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 w-24">Children</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 w-44">Preferences</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 w-28">Notes</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 w-28">Op Status</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 w-28">Cust Status</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 w-40">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleAppointments.map((a, i) => (
                    <tr key={a.appointment_id}
                      onClick={() => setSelectedAppointment(a)}
                      className={`border-b border-gray-100 hover:bg-blue-50 cursor-pointer transition ${i % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                      <td className="px-4 py-3 w-32">
                        <p className="font-semibold text-gray-800 whitespace-nowrap">{a.scheduled_date}</p>
                        <p className="text-xs text-gray-400 whitespace-nowrap">{a.scheduled_time}<FreeShoeIcon code={a.referral_code} /></p>
                        {createdLabel(a.created_at) && <p className="text-xs italic text-gray-400 whitespace-nowrap">{createdLabel(a.created_at)}</p>}
                      </td>
                      <td className="px-4 py-3 w-40">
                        <div className="flex items-center gap-1.5">
                          <p className="font-medium text-gray-800">{a.customer_name || '—'}</p>
                          <button onClick={e => { e.stopPropagation(); copyReminder(a); }} title="Copy reminder message"
                            className="text-gray-400 hover:text-zoddle-green transition shrink-0">
                            {copiedMsgId === a.appointment_id ? (
                              <span className="text-xs font-semibold text-zoddle-green">✓</span>
                            ) : (
                              <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none"
                                   viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <rect x="9" y="9" width="11" height="11" rx="2" />
                                <path d="M5 15V5a2 2 0 012-2h10" />
                              </svg>
                            )}
                          </button>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <p className="text-xs text-gray-400">{a.phone}</p>
                          {a.phone && (
                            <button onClick={e => { e.stopPropagation(); copyPhone(a); }} title="Copy phone number"
                              className="text-gray-400 hover:text-zoddle-green transition shrink-0">
                              {copiedPhoneId === a.appointment_id ? (
                                <span className="text-[10px] font-semibold text-zoddle-green">✓</span>
                              ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none"
                                     viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <rect x="9" y="9" width="11" height="11" rx="2" />
                                  <path d="M5 15V5a2 2 0 012-2h10" />
                                </svg>
                              )}
                            </button>
                          )}
                        </div>
                        <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full mt-1 inline-block ${a.is_repeat ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                          {a.is_repeat ? 'Repeat' : 'New'}
                        </span>
                      </td>
                      <td className="px-4 py-3 w-32">
                        {a.address_line_1 ? (
                          <div className="flex items-start gap-1.5">
                            <a href={mapsUrl(a.address_line_1)} target="_blank" rel="noreferrer"
                              onClick={e => e.stopPropagation()}
                              className="text-zoddle-blue text-xs hover:underline line-clamp-2 flex-1">{a.address_line_1}</a>
                            <button onClick={e => { e.stopPropagation(); copyAddress(a); }} title="Copy address"
                              className="text-gray-400 hover:text-zoddle-green transition shrink-0 mt-0.5">
                              {copiedAddrId === a.appointment_id ? (
                                <span className="text-xs font-semibold text-zoddle-green">✓</span>
                              ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none"
                                     viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <rect x="9" y="9" width="11" height="11" rx="2" />
                                  <path d="M5 15V5a2 2 0 012-2h10" />
                                </svg>
                              )}
                            </button>
                          </div>
                        ) : <span className="text-gray-300 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-3 w-24">
                        {a.children && a.children.map((c, ci) => (
                          <p key={ci} className="text-xs text-gray-700 whitespace-nowrap">{formatChild(c)}</p>
                        ))}
                      </td>
                      <td className="px-4 py-3 w-44 cursor-help"
                        onMouseEnter={e => openPrefPopover(a, e.currentTarget)}
                        onMouseLeave={() => setPrefPopover(null)}
                        onClick={e => {
                          e.stopPropagation();
                          if (prefPopover?.appt?.appointment_id === a.appointment_id) setPrefPopover(null);
                          else openPrefPopover(a, e.currentTarget);
                        }}>
                        {a.children && a.children.map((c, ci) => (
                          <p key={ci} className="text-xs text-gray-500 mb-1 truncate max-w-[150px]">
                            {c.style_pref_override ? `Styles: ${c.style_pref_override}` : <span className="text-gray-300">—</span>}
                          </p>
                        ))}
                      </td>
                      <td className="px-4 py-3 w-28">
                        <p className="text-xs text-gray-500 line-clamp-2">{a.notes || <span className="text-gray-300">—</span>}</p>
                      </td>
                      <td className="px-4 py-3 w-28">
                        <Badge value={a.operational_status} colourMap={OP_COLOURS} />
                      </td>
                      <td className="px-4 py-3 w-28">
                        <Badge value={a.customer_status} colourMap={CUST_COLOURS} />
                      </td>
                      <td className="px-4 py-3 w-40 text-xs">
                        {/* display_source disambiguates source_l1="app" into "App" vs "Website" —
                            register_booking sets that same literal value for both, see the backend's
                            display_source() docstring. Falls back to raw source_l1 if an older cached
                            response doesn't have the field yet. */}
                        {(a.display_source ?? a.source_l1) ? <span className="font-medium text-gray-600">{(a.display_source ?? a.source_l1).replace(/_/g, ' ')}</span> : <span className="text-gray-300">—</span>}
                        {a.source_l2 && (
                          <div className="flex flex-col justify-end overflow-hidden" style={{ maxHeight: '2.6em' }}
                               title={a.source_l2}>
                            <span className="text-gray-400 break-all">{a.source_l2}</span>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Preferences callout (comic speech-box style) */}
          {prefPopover && (
            <div className="fixed z-50 pointer-events-none"
                 style={{
                   top: prefPopover.top,
                   left: prefPopover.left,
                   transform: prefPopover.flip ? 'translateY(-100%)' : 'none',
                 }}>
              <div className="relative bg-white border border-gray-200 rounded-lg shadow-xl p-3 w-72 text-xs text-gray-600">
                {/* tail */}
                <div className={`absolute left-6 w-3 h-3 bg-white border-gray-200 rotate-45 ${prefPopover.flip ? '-bottom-1.5 border-r border-b' : '-top-1.5 border-l border-t'}`} />
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-2">Preferences</p>
                {(prefPopover.appt.children || []).map((c, ci) => {
                  const hasAny = c.style_pref_override || c.use_case_pref_override || c.brand_pref_override;
                  return (
                    <div key={ci} className={ci > 0 ? 'mt-2 pt-2 border-t border-gray-100' : ''}>
                      {(prefPopover.appt.children || []).length > 1 && (
                        <p className="text-[10px] font-medium text-gray-400 mb-0.5">{formatChild(c)}</p>
                      )}
                      {c.style_pref_override && <p><span className="text-gray-400">Styles:</span> {c.style_pref_override}</p>}
                      {c.use_case_pref_override && <p><span className="text-gray-400">Items:</span> {c.use_case_pref_override}</p>}
                      {c.brand_pref_override && <p><span className="text-gray-400">Brands:</span> {c.brand_pref_override}</p>}
                      {!hasAny && <p className="text-gray-300">No preferences set</p>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Errors Tab */}
      {tab === 'errors' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs text-gray-400">{errors.length} rows could not be imported.</p>
            <div className="flex items-center gap-3">
              {retryResult && (
                <span className="text-xs text-green-700 bg-green-50 px-3 py-1.5 rounded-lg font-medium">
                  {retryResult.message}
                </span>
              )}
              <button onClick={handleRetryAll} disabled={retrying || errors.length === 0}
                className="border border-gray-200 text-gray-600 text-sm font-medium px-4 py-2 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition">
                {retrying ? 'Retrying...' : 'Retry All'}
              </button>
            </div>
          </div>
          {loading ? (
            <div className="text-center py-16 text-gray-400 text-sm">Loading...</div>
          ) : errors.length === 0 ? (
            <div className="text-center py-16 text-gray-400 text-sm">No errors — all rows imported successfully</div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Timestamp</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Name</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Phone</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Date</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Time</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Gender</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Age</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Error</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {errors.map((e, i) => (
                    <tr key={e.error_id} className={`border-b border-gray-100 hover:bg-gray-50 transition ${i % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                      <td className="px-4 py-3 text-xs text-gray-500">{e.raw_timestamp || '—'}</td>
                      <td className="px-4 py-3 font-medium text-gray-800">{e.raw_parent_name || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{e.raw_phone || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{e.raw_date || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{e.raw_time || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{e.raw_gender || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{e.raw_age || '—'}</td>
                      <td className="px-4 py-3"><span className="text-xs text-zoddle-pink font-medium">{e.error_reason}</span></td>
                      <td className="px-4 py-3">
                        <button onClick={() => setEditingError(e)}
                          className="text-xs text-zoddle-blue font-medium hover:underline whitespace-nowrap">Fix</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {newBookingOpen && (
        <NewBookingModal
          onClose={() => setNewBookingOpen(false)}
          onCreated={() => { setNewBookingOpen(false); loadAppointments(); loadToday(); }}
        />
      )}
      {selectedAppointment && (
        <AppointmentDetailModal
          appointment={selectedAppointment}
          onClose={() => setSelectedAppointment(null)}
          onSaved={() => { setSelectedAppointment(null); loadAppointments(); loadToday(); }}
        />
      )}

      {editingError && (
        <ErrorCorrectionModal
          err={editingError}
          onClose={() => setEditingError(null)}
          onResolved={() => { setEditingError(null); loadErrors(); }}
        />
      )}
    </div>
  );
}
